import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useAppStore } from '@cya-routes/shared'
import { updateUserProfile } from '../../services/firebase/auth'

const sanitizeWhitespace = (value: string) => value.replace(/\s{2,}/g, ' ')

const hasTwoWords = (value: string) => value.trim().split(/\s+/).filter(Boolean).length >= 2

export function ProfileCompletionModal() {
  const { auth, setAuthState } = useAppStore()
  const { currentUser, profileNeedsCompletion } = auth

  const [firstName, setFirstName] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [error, setError] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [hasInitialized, setHasInitialized] = useState(false)

  const shouldShow = Boolean(currentUser && profileNeedsCompletion)

  useEffect(() => {
    if (shouldShow && currentUser && !hasInitialized) {
      setFirstName(currentUser.firstName || '')
      setDisplayName(currentUser.displayName || '')
      setHasInitialized(true)
    }

    if (!shouldShow && hasInitialized) {
      setHasInitialized(false)
      setError('')
    }
  }, [shouldShow, currentUser, hasInitialized])

  if (!shouldShow || !currentUser) {
    return null
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!currentUser) return

    const trimmedFirst = firstName.trim()
    const normalizedDisplay = sanitizeWhitespace(displayName).trim()

    if (!trimmedFirst) {
      setError('First name is required')
      return
    }

    if (!hasTwoWords(normalizedDisplay)) {
      setError('Display name must include at least two words. Try something like "Vince Bikeroutes".')
      return
    }

    setError('')
    setIsSaving(true)

    const derivedLastName = normalizedDisplay.split(' ').slice(1).join(' ')

    try {
      await updateUserProfile(currentUser.id, {
        firstName: trimmedFirst,
        displayName: normalizedDisplay
      })

      const updatedUser = {
        ...currentUser,
        firstName: trimmedFirst,
        lastName: derivedLastName,
        displayName: normalizedDisplay
      }

      setAuthState({
        currentUser: updatedUser,
        profileNeedsCompletion: false
      })
    } catch (err) {
      console.error('Failed to complete profile', err)
      setError('Could not save your profile right now. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  const modalContent = (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-emerald-500/40">
        <div className="px-6 py-6">
          <div className="mb-5">
            <p className="text-xs uppercase tracking-[0.2em] text-emerald-500 font-semibold">Quick setup</p>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-2">Add your rider identity</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
              We just need your first name and a two-word display name so clubs know who is sharing routes.
            </p>
          </div>

          {error && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="profile-first-name" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                First name
              </label>
              <input
                id="profile-first-name"
                type="text"
                autoComplete="given-name"
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                placeholder="Vince"
              />
            </div>

            <div>
              <label htmlFor="profile-display-name" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Display name
              </label>
              <input
                id="profile-display-name"
                type="text"
                value={displayName}
                onChange={(event) => setDisplayName(sanitizeWhitespace(event.target.value))}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                placeholder="Vince Bikeroutes"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Use something memorable—try pairing your name with a club, location, or riding style.
              </p>
            </div>

            <button
              type="submit"
              disabled={isSaving}
              className="w-full rounded-lg bg-emerald-600 py-2 text-sm font-semibold text-white shadow hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50"
            >
              {isSaving ? 'Saving profile...' : 'Save profile' }
            </button>
          </form>
        </div>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}
